/**
 * The placeholder keys a campaign may reference, and the sample values the builder previews them
 * with (PREC-4258). Moved out of the web's `getSuggestionsMap` so the API can run the builder's
 * full `validateCampaignConfig` (its `validKeys`) on a campaign written over MCP, against the same
 * key set the builder checks: two copies of "which variables exist" would disagree about whether
 * a campaign can send.
 */

/** A list column, by the two fields a placeholder key is built from. */
export interface CampaignPlaceholderColumn {
	id?: number | null;
	name: string;
}

/** A list's event-outreach details (PREC-3520), by the fields that become shared tokens. */
export interface CampaignPlaceholderEvent {
	enabled?: boolean | null;
	eventName?: string | null;
	startDate?: string | Date | null;
	endDate?: string | Date | null;
	meetingLocation?: string | null;
	meetingLink?: string | null;
	meetingTimes?: string[] | null;
	customVariables?: { key?: string | null; value?: unknown }[] | null;
}

export interface CampaignSampleSuggestionsInput {
	/** Whether the sender tokens ({senderFirstName}, {signature}, …) are offered. */
	sender?: boolean;
	columns?: CampaignPlaceholderColumn[] | null;
	event?: CampaignPlaceholderEvent | null;
	/** How an event date reads in a preview; only the values depend on it, never the keys. */
	formatDate?: (value?: string | null) => string;
}

/** The example lead the keys are derived from when no real lead is at hand. */
const SAMPLE_PROSPECT = {
	email: "example@example.com",
	name: "John Doe",
	firstName: "John",
	lastName: "Doe",
	linkedinUrl: "https://www.linkedin.com/in/john-doe/",
	title: "Software Engineer",
	headline: "Software Engineer",
	seniority: "Junior",
	jobTitleToSeniority: {
		jobTitle: "Software Engineer",
		seniority: "Junior",
	},
	city: "Berlin",
	state: "Berlin",
	country: "Germany",
	gender: "male",
	language: "german, english",
	about: "Default about",
	followers: 100,
	connections: 100,
	salutation: "Mr.", // arch-allow salutation: sample value in the variable preview
	salutationDe: "Herr", // arch-allow salutation: sample value in the variable preview
	formalNameDe: "Sehr geehrter Herr Mustermann",
	scorePersonaCategoryResult: {
		result: "P1",
		reason: "Default reason",
	},
	organization: {
		name: "Signalist",
		city: "Berlin",
		state: "Berlin",
		country: "Germany",
		industry: "Technology",
		postalCode: "12345",
		foundedYear: 2020,
		estimatedNumEmployees: 100,
		primaryDomain: "signalist.io",
		phone: "+49 123 456789",
		seoDescription: "Default SEO description",
		shortDescription: "Default short description",
		followerCount: 100,
		websiteUrl: "https://www.signalist.io",
		ScoreCompanyCategory: {
			result: "Competitor",
		},
	},
};

/** The sender tokens, with the sample values the preview shows for them. */
export const SENDER_SAMPLE_SUGGESTIONS: Readonly<Record<string, string>> = {
	senderFirstName: "Sender First Name",
	senderLastName: "Sender Last Name",
	signature: "Signature",
	unsubscribe_link: "Unsubscribe Link",
};

const defaultFormatDate = (value?: string | null): string => value ?? "";

/**
 * Adds the shared event-outreach tokens (PREC-3520) as flat keys. Keys are the FIXED token names
 * ({event_name}, {meeting_link}, …), identical across lists, so a campaign copied between lists
 * keeps working.
 */
export function addCampaignEventSuggestions(
	target: Record<string, unknown>,
	event?: CampaignPlaceholderEvent | null,
	formatDate: (value?: string | null) => string = defaultFormatDate
): void {
	if (!event?.enabled) {
		return;
	}

	const asText = (value?: string | Date | null) =>
		formatDate(value instanceof Date ? value.toISOString() : value);

	target.event_name = event.eventName ?? "";
	target.event_start = asText(event.startDate);
	target.event_end = asText(event.endDate);

	if (event.meetingLocation) {
		target.meeting_location = event.meetingLocation;
	}

	if (event.meetingLink) {
		target.meeting_link = event.meetingLink;
	}

	if (event.meetingTimes?.length) {
		target.meeting_time = event.meetingTimes[0];
	}

	event.customVariables?.forEach((variable) => {
		if (variable.key) {
			target[variable.key] = variable.value;
		}
	});
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
	Object.prototype.toString.call(value) === "[object Object]";

/**
 * Flattens nested objects into `a_b_c` keys, as `flat`'s `flatten(…, { delimiter: "_" })` does,
 * and drops the arrays the builder's suggestion map never offers as a token.
 */
export function flattenCampaignSuggestions(value: Record<string, unknown>): Record<string, unknown> {
	const flat: Record<string, unknown> = {};

	const walk = (node: unknown, prefix: string): void => {
		if (Array.isArray(node)) {
			node.forEach((item, index) => walk(item, `${prefix}_${index}`));

			return;
		}

		if (isPlainObject(node) && Object.keys(node).length > 0) {
			for (const [key, child] of Object.entries(node)) {
				walk(child, prefix ? `${prefix}_${key}` : key);
			}

			return;
		}

		flat[prefix] = node;
	};

	for (const [key, child] of Object.entries(value)) {
		if (Array.isArray(child) && child.length === 0) {
			continue;
		}

		walk(child, key);
	}

	return flat;
}

/**
 * The flattened placeholder map (key → sample value) when no lead is at hand: what the builder's
 * variable picker offers and its validation accepts.
 */
export function campaignSampleSuggestions({
	sender,
	columns,
	event,
	formatDate,
}: CampaignSampleSuggestionsInput = {}): Record<string, unknown> {
	const suggestions: Record<string, unknown> = { prospect: SAMPLE_PROSPECT };

	if (sender) {
		Object.assign(suggestions, SENDER_SAMPLE_SUGGESTIONS);
	}

	columns?.forEach((column) => {
		if (column.id) {
			suggestions[`column_${column.id}_${column.name}`] = column.name;
		}
	});

	addCampaignEventSuggestions(suggestions, event, formatDate);

	return flattenCampaignSuggestions(suggestions);
}

/** The keys a campaign of this list may reference: `validKeys` of `validateCampaignConfig`. */
export function campaignPlaceholderKeys(input: CampaignSampleSuggestionsInput = {}): string[] {
	return Object.keys(campaignSampleSuggestions(input));
}

/**
 * The key families a lead's own source data adds to the builder's variables (`getSuggestionsMap`
 * with a lead): a LinkedIn post, profile entry, event entry or company activity, a job offer, a
 * Maps business, and the list's column values. Which of their keys exist depends on the lead and
 * its source, so the sample keys cannot hold them all. A key of one of these families that the
 * sample lacks is a warning; a key of no family is a typo and blocks the campaign, or it would be
 * sent literally (PREC-4616). The other lists a lead is on are no family: their names included a
 * colleague's PRIVATE list, so `otherLists_` was removed (PREC-4910).
 */
export const CAMPAIGN_LEAD_DATA_KEY_PREFIXES: readonly string[] = [
	"liPostResult_",
	"liProfileEntryResult_",
	"liEventEntry_",
	"liCompanyActivityEntryResult_",
	"offer_",
	"mapBusiness_",
	"column_",
];

/** Whether a variable name belongs to a family of keys only some leads' sources hold. */
export function isLeadDataPlaceholder(name: string): boolean {
	return CAMPAIGN_LEAD_DATA_KEY_PREFIXES.some(
		(prefix) => name.startsWith(prefix) && name.length > prefix.length
	);
}
